import { Box, IconButton, TextField, Typography } from "@mui/material";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Check, Close, ModeEdit } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import { useUserContext } from "../../../contexts/UserContext";
import Navbar from "../../Nav";
import PageInfo from "../../PageInfo/PageInfo";
import SportButton from "../../SportButton";
import supportedSports from "../../supportedSports";
import { ImageTicTacToe, NeedLogin } from "./components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import loadingComponent from "../../LoadingComponent";

const Page = () => {
  const [isEditUsername, setIsEditUserName] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useUserContext();
  const username = profile?.user?.username;
  const isLoading = profile?.loading;
  const isAuthenticated = !!username;
  const isDesktop = window.innerWidth >= 800;
  const [newUsername, setNewUserName] = useState(username);

  const handleEditUser = () => {
    if (isEditUsername) {
      // Save on Click
      profile?.onChangeUsername?.(newUsername);
    }

    setIsEditUserName((prev) => !prev);
  };

  useEffect(() => {
    setNewUserName(username);
  }, [username]);

  return (
    <div
      style={{
        width: "100vw",
        minHeight: "100vh",
        position: "relative",
        top: 0,
        left: 0,
        overflowY: "auto",
        overflowX: "hidden",
        paddingBottom: isDesktop ? 20 : 100, // Adjusted padding for mobile devices
      }}
    >
      <PageInfo
        title="Crossover Live - Real-Time Multiplayer Sports Trivia"
        metaTitle="Crossover Live - Real-Time Multiplayer Sports Trivia | Crossover Grid"
        metaDescription="Challenge players worldwide in live multiplayer tic-tac-toe! Answer trivia across NBA, NFL, MLB, NHL, Movies & Wrestling. 24 seconds per turn. Play now!"
        metaUrl="https://crossovergrid.com/play"
        metaImage="https://crossovergrid.com/locker.jpg"
        keywords={['multiplayer game', 'sports trivia multiplayer', 'real-time challenge', 'crossover grid play', 'NBA multiplayer', 'NFL multiplayer', 'NHL multiplayer', 'MLB multiplayer', 'live trivia game']}
        canonicalLink="https://crossovergrid.com/play"
        twitterCard="summary_large_image"
        twitterTitle="Crossover Live - Real-Time Multiplayer Sports Trivia"
        twitterDescription="Challenge players worldwide in live multiplayer tic-tac-toe! Answer sports trivia in 24 seconds. 🏀🏈⚾🏒🎬🤼 Play now!"
        twitterImage="https://crossovergrid.com/locker.jpg"
        ogSiteName="Crossover Grid"
      />
      <Navbar />
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {isLoading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            width: "100vw",
          }}
        >
          {loadingComponent("Checking authentication...", null)}
        </div>
      ) : !isAuthenticated ? (
        <NeedLogin />
      ) : (
        <div
          style={{
            top: 20,
            position: "absolute",
            left: 0,
            width: "100%",
            textAlign: "center",
            margin: 0,
            display: "flex",
            flexDirection: "column",
            alignContent: "center",
            alignItems: "center",
            paddingBottom: 100,
          }}
        >
          <ImageTicTacToe />

          <Box sx={{ maxWidth: "800px", mx: "auto", px: 2, mt: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
              Welcome to Crossover Live
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                mb: 3,
              }}
            >
              <p
                style={{
                  fontSize: 26,
                  fontWeight: "bold",
                  margin: 0,
                  display: "flex",
                  flexDirection: "row",
                  gap: "1rem",
                  alignItems: "center",
                }}
                className={`sign-font`}
              >
                {!isEditUsername && <>{username ?? ""}</>}
                {!isEditUsername && (
                  <IconButton onClick={handleEditUser}>
                    <ModeEdit />
                  </IconButton>
                )}
                {!!isEditUsername && (
                  <TextField
                    autoFocus
                    defaultValue={username}
                    variant="filled"
                    onChange={(e) => setNewUserName(e.target.value)}
                  />
                )}
                {!!isEditUsername && (
                  <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                    <IconButton
                      onClick={handleEditUser}
                      sx={{ border: "1px solid #fff" }}
                    >
                      <Check />
                    </IconButton>
                    <IconButton
                      onClick={() => setIsEditUserName(false)}
                      sx={{ border: "1px solid #fff" }}
                    >
                      <Close />
                    </IconButton>
                  </Box>
                )}
              </p>
            </Box>

            <Typography
              sx={{
                fontSize: 18,
                fontWeight: "500",
                mb: 2,
              }}
            >
              Choose a Sport to Start
            </Typography>
          </Box>

          <div
            style={{
              marginTop: 20,
              display: "flex",
              flexDirection: "row",
              flexFlow: "row wrap",
              width: "100vw",
              alignItems: "center",
              justifyContent: "center",
              gap: isDesktop ? 25 : 10,
              maxWidth: "900px",
            }}
          >
            {supportedSports.map((sport, index) => (
              <SportButton
                key={sport.id}
                index={index}
                sport={sport}
                onClick={() =>
                  navigate(`/play/waiting`, { state: { sport_id: sport.id } })
                }
              />
            ))}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              marginTop: "30px",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => navigate("/user/stats")}
              style={styles.button}
            >
              View Your Stats
            </button>
            <button
              onClick={() => navigate("/leaderboard")}
              style={styles.button}
            >
              View Leaderboard
            </button>
          </div>

          <Box sx={{ maxWidth: "800px", mx: "auto", px: 2, mt: 5, mb: 4 }}>
            <Box
              sx={{
                backgroundColor: "rgba(70, 130, 180, 0.1)",
                borderRadius: "12px",
                padding: "24px",
                border: "1px solid rgba(70, 130, 180, 0.3)",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "600", mb: 2 }}>
                🎮 How to Play
              </Typography>
              <Box sx={{ textAlign: "left", mx: "auto", maxWidth: "600px" }}>
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: "15px", mb: 0.5 }}>
                    <strong>1. Choose Your Sport</strong>
                  </Typography>
                  <Typography sx={{ fontSize: "14px", opacity: 0.85 }}>
                    Select from NBA, NFL, MLB, NHL, Soccer, or Movies
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: "15px", mb: 0.5 }}>
                    <strong>2. Get Matched</strong>
                  </Typography>
                  <Typography sx={{ fontSize: "14px", opacity: 0.85 }}>
                    You'll be instantly paired with an opponent
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: "15px", mb: 0.5 }}>
                    <strong>3. Play Tic-Tac-Toe</strong>
                  </Typography>
                  <Typography sx={{ fontSize: "14px", opacity: 0.85 }}>
                    Answer trivia correctly to claim squares. First to 3-in-a-row wins!
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "15px", mb: 0.5 }}>
                    <strong>4. Each Turn = 24 Seconds</strong>
                  </Typography>
                  <Typography sx={{ fontSize: "14px", opacity: 0.85 }}>
                    Think fast! Time out and you lose your turn
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </div>
      )}
    </div>
  );
};

const styles = {
  button: {
    backgroundColor: "#4682B4", // Complementary color to #A3E3BA
    color: "#ffffff",
    border: "none",
    padding: "10px 20px",
    borderRadius: 4,
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: "16px",
    transition: "background-color 0.3s ease",
    margin: "0 5px",
  },
};

export const LockerRoom = Page;
export default LockerRoom;
